import { ApiClient, ApiError } from "./ApiClient";
import { CategoryApi } from "./CategoryApi";
import { ProductApi } from "./ProductsApi";
import { ImportPayloadRow, ImportProgress, ImportSummary } from "@utils/import/core/types";
import { IMPORT_BATCH_SIZE, parseFailures, runImportInBatches } from "@utils/import/core/sendBatches";
import { getUserData } from "@utils/Auth";

export { IMPORT_BATCH_SIZE };
export type { ImportProgress, ImportSummary };

/**
 * Envia los productos a la lambda de importacion en lotes secuenciales.
 *
 * `userLabel` y `userEmail` viajan una vez por lote porque la lambda los necesita para
 * firmar los movimientos de inventario y los registros de costo, y en el evento de AppSync
 * solo llega el tenant.
 */
export async function importProducts(
    rows: ImportPayloadRow[],
    onProgress?: (progress: ImportProgress) => void
): Promise<ImportSummary> {
    const { email: userEmail = "", name = "" } = await getUserData();
    const userLabel = name || userEmail;

    const summary = await runImportInBatches(
        rows,
        async (batch) => {
            const { data, errors } = await ApiClient.mutations.importProductsMutation({
                rows: JSON.stringify(batch),
                userLabel,
                userEmail,
            });

            if (errors?.length) {
                throw new ApiError("Error al importar los productos", "API_ERROR", errors);
            }
            if (!data?.ok) {
                throw new ApiError(data?.error || "No fue posible importar los productos", "API_ERROR");
            }

            return {
                createdCount: data.createdCount || 0,
                updatedCount: data.updatedCount || 0,
                failures: parseFailures(data.failed),
            };
        },
        onProgress
    );

    // La lambda escribe directo en DynamoDB: las caches locales de GenericModelApi no se
    // enteran. `Category` esta en CACHEABLE_MODELS por las categorias recien creadas.
    await CategoryApi.invalidateAllByTenant();
    CategoryApi.clearCache();
    ProductApi.clearCache();

    return summary;
}
